import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { io } from "socket.io-client";
import { PublicEventAPI } from "../services/publicEvent.api";
import "./HomepageLatestEvent.scss";

/* ===============================
   SOCKET (DEV + PROD SAFE)
=============================== */
const socket = io(
  process.env.REACT_APP_API_URL || "http://localhost:5003",
  { transports: ["websocket"] }
);

/* ======================================================
   SAFE STATS
====================================================== */
function getStats(event) {
  const total =
    event.ticketTiers?.reduce(
      (sum, t) => sum + Number(t.quota || 0),
      0
    ) || 0;

  const sold =
    typeof event.sold === "number"
      ? event.sold
      : 0;

  const remaining =
    typeof event.remaining === "number"
      ? event.remaining
      : Math.max(total - sold, 0);

  const percent =
    typeof event.percent === "number"
      ? event.percent
      : total > 0
      ? Math.round((sold / total) * 100)
      : 0;

  const minPrice =
    event.ticketTiers?.length > 0
      ? Math.min(...event.ticketTiers.map((t) => t.price))
      : 0;

  return { remaining, minPrice, percent };
}

function getCountdown(date) {
  const diff = new Date(date) - new Date();
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function HomepageLatestEvent() {
  const [featured, setFeatured] = useState(null);
  const [events, setEvents] = useState([]);
  const [countdown, setCountdown] = useState(null);

  /* ======================================================
     FETCH EVENTS
  ====================================================== */
  const fetchEvents = async () => {
    const res = await PublicEventAPI.list();
    const data = res.data;

    if (!data?.length) return;

    const now = moment();

    const sorted = [...data].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const upcoming = sorted.filter((e) =>
      moment(e.date).isSameOrAfter(now, "day")
    );

    const past = sorted
      .filter((e) => moment(e.date).isBefore(now, "day"))
      .reverse();

    const ordered = [...upcoming, ...past];

    setFeatured(ordered[0]);
    setEvents(ordered.slice(1));
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  /* ======================================================
     REALTIME UPDATE (PROPER FIX)
  ====================================================== */
  useEffect(() => {
    const handleTicketUpdate = async ({ eventId }) => {
      if (!eventId) return;

      try {
        const res = await PublicEventAPI.getTicketStats(eventId);
        const stats = res.data.stats;

        const totalSold = stats.reduce(
          (sum, s) => sum + s.sold,
          0
        );

        const totalQuota = stats.reduce(
          (sum, s) => sum + s.quota,
          0
        );

        const updatedFields = {
          sold: totalSold,
          remaining: Math.max(totalQuota - totalSold, 0),
          percent:
            totalQuota > 0
              ? Math.round((totalSold / totalQuota) * 100)
              : 0,
        };

        // Update featured
        setFeatured((prev) =>
          prev && prev._id === eventId
            ? { ...prev, ...updatedFields }
            : prev
        );

        // Update side list
        setEvents((prev) =>
          prev.map((e) =>
            e._id === eventId
              ? { ...e, ...updatedFields }
              : e
          )
        );

      } catch (err) {
        console.error("Realtime update failed:", err);
      }
    };

    socket.on("event:ticket-updated", handleTicketUpdate);

    return () => {
      socket.off("event:ticket-updated", handleTicketUpdate);
    };
  }, []);

  /* ======================================================
     COUNTDOWN (only for single-event hero mode)
  ====================================================== */
  useEffect(() => {
    if (!featured || events.length > 0) return;
    const tick = () => setCountdown(getCountdown(featured.date));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [featured, events.length]);

  if (!featured) return null;

  const stats = getStats(featured);
  const isUpcoming = moment(featured.date).isAfter(moment());

  const image =
    featured.headerImage?.url ||
    "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=1600";

  /* ── Single event: hero banner mode ── */
  if (events.length === 0) {
    return (
      <section className="md-section" id="events">
        <div className="container">
          <div className="md-header">
            <span className="md-overline">Event Terbaru</span>
            <h2>Event &amp; Kegiatan</h2>
            <p>Temukan event mendatang dan kegiatan terbaru Roundtable Community GKY Greenville</p>
          </div>

          <div
            className="md-hero-banner"
            style={{ backgroundImage: `url(${image})` }}
          >
            <div className="md-hero-overlay" />
            <div className="md-hero-content">
              {/* ── Left col ── */}
              <div className="md-hero-left">
                <span className="md-hero-badge">
                  {isUpcoming ? "Event Terdekat" : "Event Terbaru"}
                </span>

                <h3 className="md-hero-title">{featured.title}</h3>

                <div className="md-hero-meta">
                  <span>📅</span>
                  {moment(featured.date).format("D MMMM YYYY")}
                  {(featured.timeStart || featured.timeEnd) && (
                    <> &middot; {featured.timeStart}{featured.timeEnd ? `–${featured.timeEnd}` : ""}</>
                  )}
                  {featured.location?.name && (
                    <> &middot; {featured.location.name}</>
                  )}
                </div>

                <div className="md-hero-bottom">
                  <span className="md-hero-price">
                    Rp {stats.minPrice.toLocaleString("id-ID")}
                  </span>
                  <Link to={`/events/${featured.slug}`} className="md-hero-cta">
                    Lihat Event &rarr;
                  </Link>
                </div>
              </div>

              {/* ── Right col: countdown ── */}
              {countdown && (
                <div className="md-hero-right">
                  <div className="md-hero-countdown">
                    <div className="md-hero-countdown-label">Mulai dalam</div>
                    <div className="md-hero-countdown-grid">
                      <div className="md-hero-unit">
                        <span className="md-hero-num">{pad(countdown.d)}</span>
                        <span className="md-hero-lbl">hari</span>
                      </div>
                      <div className="md-hero-sep">:</div>
                      <div className="md-hero-unit">
                        <span className="md-hero-num">{pad(countdown.h)}</span>
                        <span className="md-hero-lbl">jam</span>
                      </div>
                      <div className="md-hero-sep">:</div>
                      <div className="md-hero-unit">
                        <span className="md-hero-num">{pad(countdown.m)}</span>
                        <span className="md-hero-lbl">menit</span>
                      </div>
                      <div className="md-hero-sep">:</div>
                      <div className="md-hero-unit">
                        <span className="md-hero-num">{pad(countdown.s)}</span>
                        <span className="md-hero-lbl">detik</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ── Multiple events: grid mode ── */
  return (
    <section className="md-section" id="events">
      <div className="container">

        <div className="md-header">
          <span className="md-overline">Event Terbaru</span>
          <h2>Event &amp; Kegiatan</h2>
          <p>Temukan event mendatang dan kegiatan terbaru GKY — retreat, pembinaan, dan ibadah.</p>
        </div>

        <div className="md-grid">

          {/* ================= FEATURED ================= */}
          <div className="md-featured-card">
            <div className="md-image">
              <img src={image} alt={featured.title} />
              <span
                className={`md-status ${
                  isUpcoming ? "upcoming" : "past"
                }`}
              >
                {isUpcoming ? "Upcoming" : "Past"}
              </span>
            </div>

            <div className="md-content">
              <h3>{featured.title}</h3>

              <div className="md-meta">
                {moment(featured.date).format("D MMMM YYYY")}
                {(featured.timeStart || featured.timeEnd) && (
                  <> &middot; {featured.timeStart}{featured.timeEnd ? `–${featured.timeEnd}` : ""}</>
                )}
                {" • "}{featured.location?.name || "Location TBA"}
              </div>

              <div className="md-footer">
                <div className="md-price">
                  Rp {stats.minPrice.toLocaleString("id-ID")}
                </div>

                <Link
                  to={`/events/${featured.slug}`}
                  className="md-btn"
                >
                  View Details
                </Link>
              </div>
            </div>
          </div>

          {/* ================= SIDE ================= */}
          <div className="md-side">
            <div className="md-side-header">
              <h4>Other Events</h4>
            </div>

            <div className="md-side-scroll">
              {events.map((event) => {
                const s = getStats(event);
                const isUpcoming = moment(event.date).isAfter(moment());

                const img =
                  event.headerImage?.url ||
                  "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=800";

                return (
                  <Link
                    key={event._id}
                    to={`/events/${event.slug}`}
                    className={`md-mini-card ${
                      !isUpcoming ? "past" : ""
                    }`}
                  >
                    <div className="mini-image">
                      <img src={img} alt={event.title} />
                      {!isUpcoming && (
                        <div className="mini-overlay">
                          Past
                        </div>
                      )}
                    </div>

                    <div className="mini-content">
                      <div className="mini-title">
                        {event.title}
                      </div>

                      <div className="mini-meta">
                        {moment(event.date).format("D MMM YYYY")}
                        {(event.timeStart || event.timeEnd) && (
                          <> · {event.timeStart}{event.timeEnd ? `–${event.timeEnd}` : ""}</>
                        )}
                      </div>

                      <div className="mini-bottom">
                        <span className="mini-price">
                          Rp {s.minPrice.toLocaleString("id-ID")}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}